"use client";
import React, { createContext, useContext, useState, useEffect } from 'react';

interface MutedCard {
  id: string;
  page: 'movement' | 'network';
  title: string;
  mutedAt: Date;
  reason?: string;
}

interface MutedUser {
  userId: string;
  userName: string;
  mutedAt: Date;
  reason?: string;
}

interface AdminMuteContextType {
  mutedCards: MutedCard[];
  mutedUsers: MutedUser[];
  isAdmin: boolean;
  muteCard: (cardId: string, page: 'movement' | 'network', title: string, reason?: string) => void;
  unmuteCard: (cardId: string) => void;
  isCardMuted: (cardId: string) => boolean;
  getMutedCardsForPage: (page: 'movement' | 'network') => MutedCard[];
  muteUser: (userId: string, userName: string, reason?: string) => void;
  unmuteUser: (userId: string) => void;
  isUserMuted: (userId: string) => boolean;
}

const AdminMuteContext = createContext<AdminMuteContextType | undefined>(undefined);

export const useAdminMute = () => {
  const context = useContext(AdminMuteContext);
  if (!context) {
    throw new Error('useAdminMute must be used within an AdminMuteProvider');
  }
  return context;
};

interface AdminMuteProviderProps {
  children: React.ReactNode;
  isAdmin: boolean;
}

export const AdminMuteProvider: React.FC<AdminMuteProviderProps> = ({ children, isAdmin }) => {
  const [mutedCards, setMutedCards] = useState<MutedCard[]>(() => {
    try {
      const saved = localStorage.getItem('adminMutedCards');
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.map((card: any) => ({
          ...card,
          mutedAt: new Date(card.mutedAt)
        }));
      }
    } catch (error) {
      console.error('Error loading muted cards:', error);
    }
    return [];
  });

  const [mutedUsers, setMutedUsers] = useState<MutedUser[]>(() => {
    try {
      const saved = localStorage.getItem('adminMutedUsers');
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.map((user: any) => ({
          ...user,
          mutedAt: new Date(user.mutedAt)
        }));
      }
    } catch (error) {
      console.error('Error loading muted users:', error);
    }
    return [];
  });

  useEffect(() => {
    try {
      localStorage.setItem('adminMutedCards', JSON.stringify(mutedCards));
    } catch (error) {
      console.error('Error saving muted cards:', error);
    }
  }, [mutedCards]);

  useEffect(() => {
    try {
      localStorage.setItem('adminMutedUsers', JSON.stringify(mutedUsers));
    } catch (error) {
      console.error('Error saving muted users:', error);
    }
  }, [mutedUsers]);

  const muteCard = (cardId: string, page: 'movement' | 'network', title: string, reason?: string) => {
    if (!isAdmin) return;
    
    setMutedCards(prev => {
      const exists = prev.find(card => card.id === cardId);
      if (exists) return prev;
      
      return [...prev, {
        id: cardId,
        page,
        title,
        mutedAt: new Date(),
        reason
      }];
    });
  };

  const unmuteCard = (cardId: string) => {
    if (!isAdmin) return;
    
    setMutedCards(prev => prev.filter(card => card.id !== cardId));
  };

  const isCardMuted = (cardId: string) => {
    return mutedCards.some(card => card.id === cardId);
  };

  const getMutedCardsForPage = (page: 'movement' | 'network') => {
    return mutedCards.filter(card => card.page === page);
  };

  const muteUser = (userId: string, userName: string, reason?: string) => {
    if (!isAdmin) return;
    
    setMutedUsers(prev => {
      const exists = prev.find(user => user.userId === userId);
      if (exists) return prev;
      
      return [...prev, {
        userId,
        userName,
        mutedAt: new Date(),
        reason
      }];
    });
  };

  const unmuteUser = (userId: string) => {
    if (!isAdmin) return;
    
    setMutedUsers(prev => prev.filter(user => user.userId !== userId));
  };

  const isUserMuted = (userId: string) => {
    return mutedUsers.some(user => user.userId === userId);
  };

  const value: AdminMuteContextType = {
    mutedCards,
    mutedUsers,
    isAdmin,
    muteCard,
    unmuteCard,
    isCardMuted,
    getMutedCardsForPage,
    muteUser,
    unmuteUser,
    isUserMuted
  };

  return (
    <AdminMuteContext.Provider value={value}>
      {children}
    </AdminMuteContext.Provider>
  );
};

















